var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useState } from 'react';
import { UXPContext } from '@core/context/Context';
import { createWidgetInstances } from './widgetDrawerUtils';
import WidgetDrawerContent from './WidgetDrawerContent';
import Modal from '@widget/popups/Modal';
import useToast from '@notifications/Toast/useToast';
import { wait, isWidget } from '@utils';
import { LoadingFeedback } from '@widget/loaders';
/**
 * @export
 */
export var WidgetDrawerMode;
(function (WidgetDrawerMode) {
    WidgetDrawerMode["Single"] = "single";
    WidgetDrawerMode["Multiple"] = "multi";
})(WidgetDrawerMode || (WidgetDrawerMode = {}));
/**
 * @export
 */
export var WidgetDrawerType;
(function (WidgetDrawerType) {
    WidgetDrawerType["Widgets"] = "widgets";
    WidgetDrawerType["UIs"] = "uis";
})(WidgetDrawerType || (WidgetDrawerType = {}));
/**
 * @export
 */
export var WidgetDrawerStatus;
(function (WidgetDrawerStatus) {
    WidgetDrawerStatus["All"] = "all";
    WidgetDrawerStatus["Installed"] = "installed";
    WidgetDrawerStatus["Added"] = "added";
    WidgetDrawerStatus["NotInstalled"] = "not-installed";
})(WidgetDrawerStatus || (WidgetDrawerStatus = {}));
/**
 * @export
 * Widget drawer modal wrapper component that handles widget initialization internally
 *
 * @example
 * ```tsx
 * <WidgetDrawer
 *   show={showDrawer}
 *   onClose={() => setShowDrawer(false)}
 *   widgets={dashboardWidgets}
 *   layouts={dashboardLayouts}
 *   onChange={(event) => {
 *     if (event.action === 'select') {
 *       // Raw selection (no layout processing)
 *       handleRawSelection(event.items)
 *     } else {
 *       // Add or delete (with layout processing)
 *       setWidgets(event.widgets!)
 *       setLayouts(event.layouts!)
 *     }
 *     return true
 *   }}
 *   config={{ mode: 'multi' }}
 * />
 * ```
 */
export const WidgetDrawer = ({ show, onClose, widgets, layouts, onChange, isBounded = false, maxColumns = 40, config, orientation }) => {
    const context = useContext(UXPContext);
    const toast = useToast();
    const [processing, setProcessing] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');
    const handleChange = useCallback((changeset) => __awaiter(void 0, void 0, void 0, function* () {
        const _type = ((changeset === null || changeset === void 0 ? void 0 : changeset.type) || (config === null || config === void 0 ? void 0 : config.defaultType) || WidgetDrawerType.Widgets);
        // Handle raw data mode (select action), or UIs which are always passed through raw
        if (((config === null || config === void 0 ? void 0 : config.returnRawData) || _type === WidgetDrawerType.UIs) && changeset.selected && changeset.selected.length > 0) {
            const success = yield onChange({
                action: 'select',
                items: changeset.selected,
                type: _type
            });
            if (success !== false) {
                onClose();
            }
            return;
        }
        setProcessing(true);
        let updatedWidgets = [...widgets];
        let updatedLayouts = Object.assign({}, layouts);
        // Handle widget removal
        if (changeset.deleted && changeset.deleted.length > 0) {
            setProgressMessage('Removing widget instances...');
            const deletedIds = changeset.deleted.map((w) => w.id);
            updatedWidgets = updatedWidgets.filter(w => !deletedIds.includes(w.id));
            // Remove from layouts
            Object.keys(updatedLayouts).forEach(breakpoint => {
                var _a;
                updatedLayouts[breakpoint] = (_a = updatedLayouts === null || updatedLayouts === void 0 ? void 0 : updatedLayouts[breakpoint]) === null || _a === void 0 ? void 0 : _a.filter(layout => !deletedIds.includes(layout.i));
            });
            const success = yield onChange({
                action: 'delete',
                items: changeset.deleted,
                type: _type,
                widgets: updatedWidgets,
                layouts: updatedLayouts
            });
            if (success !== false) {
                toast.success(context.$L('uxp-core.text.widget-removed'));
                setProcessing(false);
                return;
            }
        }
        // Handle widget addition (only widgets, not UIs - UIs need raw mode)
        if (changeset.selected && changeset.selected.length > 0) {
            const selectedWidgets = changeset.selected.filter((item) => isWidget(item === null || item === void 0 ? void 0 : item.id));
            if (selectedWidgets.length === 0) {
                toast.warning(context.$L('uxp-core.text.no-widgets-selected'));
                setProcessing(false);
                return;
            }
            setProgressMessage('Loading scripts...');
            yield wait(200);
            setProgressMessage('Preparing widgets...');
            // Create widget instances with proper positioning
            const newInstances = yield createWidgetInstances(selectedWidgets, updatedWidgets, {
                context,
                direction: context.direction,
                restrictedMode: isBounded,
                maxColumns: maxColumns,
                orientation: orientation
            });
            if (newInstances.length > 0) {
                setProgressMessage('Almost ready...');
                updatedWidgets = [...updatedWidgets, ...newInstances];
                // Update layouts for all breakpoints
                const newLayouts = newInstances.map(w => w.layout);
                Object.keys(updatedLayouts).forEach(breakpoint => {
                    updatedLayouts[breakpoint] = [
                        ...(updatedLayouts[breakpoint] || []),
                        ...newLayouts
                    ];
                });
                const success = yield onChange({
                    action: 'add',
                    items: changeset.selected,
                    type: _type,
                    widgets: updatedWidgets,
                    layouts: updatedLayouts
                });
                if (success) {
                    const count = newInstances.length;
                    toast.success(context.$L('uxp-core.text.new-widgets-added', { count }));
                    onClose();
                }
                else {
                    toast.error(context.$L('uxp-core.text.unable-to-add-widgets'));
                }
                setProcessing(false);
            }
        }
        setProcessing(false);
    }), [widgets, layouts, onChange, context, toast, onClose, config, isBounded, maxColumns]);
    return (_jsxs(_Fragment, { children: [_jsx(Modal, { show: show, onClose: onClose, className: "uxpcore_widgetdrawer__drawer", title: (config === null || config === void 0 ? void 0 : config.title) || context.$L('uxp-core.wm.title'), animation: 'slide-from-top-right', children: _jsx(WidgetDrawerContent, { onChange: handleChange, existingWidgets: widgets, onClose: onClose, config: config }) }), _jsx(LoadingFeedback, { show: processing, message: progressMessage, submessage: context.$L('uxp-core.text.please-wait') })] }));
};
export default WidgetDrawer;
